// Side-effect: register Tabulator modules first — `extendModule` below
// silently no-ops if the named module hasn't been registered yet.
import './register';

import { Tabulator } from 'tabulator-tables';
import { t } from '../../javascript/lib/i18n';
import { extractDatetimeAttr, extractSortValue } from './helpers';

// Custom/overridden sorters.
//
// Cell values are stored as rendered HTML so display (links, badges,
// components) is preserved. That means the default string/number sorters
// would compare markup instead of content. Our sorters extract a
// comparable key from the HTML first:
//
// - temporal: reads `<time datetime="...">` for chronological sort,
//   regardless of display format (e.g. "Today | 9:17 am").
// - string / number: read a `data-sort-value` attribute off a
//   `<span data-sort-value="...">` wrapper the controller adds when the
//   source <td> carries a `data-value` attribute. Lets callers show one
//   thing (e.g. "High") and sort by another (e.g. "2").
Tabulator.extendModule('sort', 'sorters', {
  temporal(a, b) {
    const aKey = extractDatetimeAttr(a);
    const bKey = extractDatetimeAttr(b);
    if (aKey < bKey) return -1;
    if (aKey > bKey) return 1;
    return 0;
  },
  string(a, b) {
    return compareString(extractSortValue(a), extractSortValue(b));
  },
  number(a, b) {
    return compareNumber(extractSortValue(a), extractSortValue(b));
  },
});

function compareString(a, b) {
  return String(a ?? '')
    .toLowerCase()
    .localeCompare(String(b ?? '').toLowerCase());
}

function compareNumber(a, b) {
  const aNum = parseFloat(a);
  const bNum = parseFloat(b);
  const aInvalid = Number.isNaN(aNum);
  const bInvalid = Number.isNaN(bNum);
  if (aInvalid && bInvalid) return 0;
  if (aInvalid) return -1;
  if (bInvalid) return 1;
  return aNum - bNum;
}

// Tabulator renders sort buttons as plain <div>s — not keyboard-focusable
// and not announced as buttons by screen readers. Upgrade each to an ARIA
// button so keyboard users can tab to and activate them, and screen readers
// announce them properly.
export function enhanceSortButtons(containerEl) {
  containerEl
    .querySelectorAll('.tabulator-col-sorter-element')
    .forEach((btn) => {
      btn.setAttribute('role', 'button');
      btn.setAttribute('tabindex', '0');

      btn.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          btn.click();
        }
      });
    });
  refreshSortLabels(containerEl);
}

// Keep the sort button's aria-label in sync with the current sort state.
// The column's `aria-sort` attribute is the canonical signal, but it sits
// on the parent column; screen readers in focus mode don't propagate it to
// the button itself. Embedding the state in the button's label ensures
// keyboard users tabbing to the button hear the current direction.
export function refreshSortLabels(containerEl) {
  containerEl
    .querySelectorAll('.tabulator-col-sorter-element')
    .forEach((btn) => {
      const col = btn.closest('.tabulator-col');
      const title = col
        ?.querySelector('.tabulator-col-title')
        ?.textContent.trim();
      if (!title) return;

      const state = col.getAttribute('aria-sort');
      const label =
        state === 'ascending'
          ? t('js.tabulator_table.sorters.sort_by_ascending', { title })
          : state === 'descending'
          ? t('js.tabulator_table.sorters.sort_by_descending', { title })
          : t('js.tabulator_table.sorters.sort_by', { title });
      btn.setAttribute('aria-label', label);
    });
}
